<template>
    <v-dialog v-if="type" v-model="show" :max-width="1200" persistent :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
            <v-card-title class="text-h6">{{ title }} bearbeiten</v-card-title>
            <v-card-text>
                <v-form v-model="valid" ref="form">
                    <v-row class="mt-2">
                        <v-col class="my-0 pt-0 pb-0">
                            <!-- Checkbox state managed locally via localEditedItem to ensure cancel truly discards changes -->
                            <v-checkbox :input-value="localEditedItem && localEditedItem.rechnungs_empfänger === type" 
                                @change="updateLocalRecipient"
                                label="Als Rechnungsempfänger festlegen" dense></v-checkbox>
                        </v-col>
                        <v-col class="my-0 pt-0 pb-0">
                            <!-- Checkbox state managed locally via localEditedItem to ensure cancel truly discards changes -->
                            <v-checkbox :input-value="localEditedItem && localEditedItem.mitversichert_bei === type" 
                                @change="updateLocalInsuredWith"
                                :label="`Klient ist bei ${title} mitversichert`" dense></v-checkbox>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="my-0 py-0" cols="12" sm="12">

                            <!-- Section: Link existing client -->
                            <v-card outlined class="pa-3 mb-2">
                                <!-- Linking Section (always visible) -->
                                <div class="d-flex align-center mb-2">
                                    <v-icon left color="primary">mdi-link-variant</v-icon>
                                    <span class="font-weight-medium">Anderen Klienten als {{ title }} verknüpfen</span>
                                </div>
                                <div class="mb-2 text-caption">Wähle einen bestehenden Klienten aus, um ihn als {{ title }} zu verknüpfen.</div>
                                <v-autocomplete
                                    v-if="relationshipTypes.includes(type)"
                                    v-model="linkedClient[type]"
                                    :items="clientOptionsWithDisplayName"
                                    :search-input.sync="searchLinkedClient[type]"
                                    :item-disabled="item => item.disabled"
                                    item-text="displayName"
                                    item-value="id"
                                    label="Klient auswählen"
                                    dense outlined
                                    clearable
                                    clear-icon="mdi-close-circle"
                                    hide-details
                                    @change="onLinkedClientChange(type)"
                                >
                                    <template v-slot:item="{ item }">
                                        <div class="d-flex align-center justify-space-between w-100">
                                            <!-- Client name -->
                                            <span :class="{ 'text--disabled': item.disabled }">
                                                {{ item.displayName }}
                                            </span>
                                            
                                            <!-- Status indicators -->
                                            <div class="d-flex align-center">
                                                <!-- Existing relationship chips -->
                                                <v-chip 
                                                    v-for="status in item.statusChips" 
                                                    :key="status.type"
                                                    small 
                                                    class="ml-2">
                                                    <v-icon left x-small>{{ status.icon }}</v-icon>
                                                    {{ status.label }}
                                                </v-chip>
                                                
                                                <!-- Conflict warning icon -->
                                                <div v-if="item.disabled" class="ml-2" style="pointer-events: auto;">
                                                    <v-tooltip bottom>
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-icon 
                                                                v-bind="attrs"
                                                                v-on="on">
                                                                mdi-alert-circle-outline
                                                            </v-icon>
                                                        </template>
                                                        <div style="max-width: 300px;">
                                                            <div v-for="conflict in item.conflicts" :key="conflict" class="text-caption">
                                                                {{ conflict }}
                                                            </div>
                                                        </div>
                                                    </v-tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:selection="{ item }">
                                        <div class="d-flex align-center">
                                            <span>{{ item.displayName }}</span>
                                            <v-chip 
                                                v-for="status in item.statusChips" 
                                                :key="status.type"
                                                small 
                                                class="ml-2">
                                                <v-icon left x-small>{{ status.icon }}</v-icon>
                                                {{ status.label }}
                                            </v-chip>
                                        </div>
                                    </template>
                                </v-autocomplete>

                                <v-alert
                                    v-if="showPartnerWarning"
                                    type="warning"
                                    dense
                                    outlined
                                    prominent
                                    class="mt-2 mb-2"
                                >
                                <template v-if="editedItem.fk_partner_id && linkedClient.partner && editedItem.fk_partner_id !== linkedClient.partner">
                                    Die bestehende Partner-Verknüpfung zwischen
                                    <strong>{{ currentClientName }}</strong>
                                    und
                                    <strong>{{ previousPartnerName }}</strong>
                                    wird entfernt, wenn du speicherst!
                                </template>
                                <template v-else-if="!editedItem.fk_partner_id && linkedClient.partner && selectedPartnerCurrentPartnerName">
                                    Der ausgewählte Partner
                                    <strong>{{ clientOptionsWithDisplayName.find(c => c.id === linkedClient.partner)?.vorname }} {{ clientOptionsWithDisplayName.find(c => c.id === linkedClient.partner)?.nachname }}</strong>
                                    ist bereits mit
                                    <strong>{{ selectedPartnerCurrentPartnerName }}</strong>
                                    verknüpft. Diese Verknüpfung wird entfernt, wenn du speicherst!
                                </template>        
                            </v-alert>


                                <v-divider class="my-4" />

                                <!-- Linked Client Info (if linked) -->
                                <template v-if="linkedClient[type]">
                                    <v-alert
                                        type="info"
                                        class="mt-2 mb-2"
                                        dense
                                    >
                                        <v-row align="center">
                                            <v-col class="grow">
                                                Die Daten werden vom verknüpften Klienten übernommen. Änderungen sind nur dort möglich.
                                            </v-col>
                                            <v-col class="shrink">
                                                <v-btn small outlined @click="goToLinkedClient(linkedClient[type])">
                                                    Zum Klienten navigieren
                                                    <v-icon right>mdi-open-in-new</v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-alert>
                                    <v-list dense outlined class="mt-2">
                                        <v-list-item>
                                            <v-list-item-icon class="mr-3">
                                                <v-icon>mdi-account</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content
                                                @mouseenter="setCopyIconHover('name', true)"
                                                @mouseleave="setCopyIconHover('name', false)"
                                            >
                                                <v-list-item-title class="d-flex align-center">
                                                    <span class="font-weight-bold mr-2">Name:</span>
                                                    <span>{{ getLinkedClientName() }}</span>
                                                    
                                                    <!-- Copy icon for name -->
                                                    <v-btn
                                                        v-if="getLinkedClientName() && isClipboardSupported"
                                                        icon
                                                        small
                                                        class="ml-2"
                                                        :style="{ 
                                                            opacity: copyIconHover.name ? 1 : 0,
                                                            transition: 'opacity 0.2s ease'
                                                        }"
                                                        :color="copySuccessState.name ? 'green' : $store.state.theme.primary"
                                                        @click="copyToClipboard(getLinkedClientName(), 'name')"
                                                        @mouseenter="setCopyIconHover('name', true)"
                                                        @mouseleave="setCopyIconHover('name', false)"
                                                    >
                                                        <v-icon small>
                                                            {{ copySuccessState.name ? 'mdi-check' : 'mdi-content-copy' }}
                                                        </v-icon>
                                                    </v-btn>
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-icon class="mr-3">
                                                <v-icon>mdi-home-city-outline</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content
                                                @mouseenter="setCopyIconHover('address', true)"
                                                @mouseleave="setCopyIconHover('address', false)"
                                            >
                                                <v-list-item-title class="d-flex align-center">
                                                    <span class="font-weight-bold mr-2">Adresse:</span>
                                                    <span>{{ currentAngehoerigeData('adresse') }}, {{ currentAngehoerigeData('plz') }} {{ currentAngehoerigeData('ort') }}</span>
                                                    
                                                    <!-- Copy icon for address -->
                                                    <v-btn
                                                        v-if="(currentAngehoerigeData('adresse') || currentAngehoerigeData('plz') || currentAngehoerigeData('ort')) && isClipboardSupported"
                                                        icon
                                                        small
                                                        class="ml-2"
                                                        :style="{ 
                                                            opacity: copyIconHover.address ? 1 : 0,
                                                            transition: 'opacity 0.2s ease'
                                                        }"
                                                        :color="copySuccessState.address ? 'green' : $store.state.theme.primary"
                                                        @click="copyToClipboard(`${currentAngehoerigeData('adresse')}, ${currentAngehoerigeData('plz')} ${currentAngehoerigeData('ort')}`, 'address')"
                                                        @mouseenter="setCopyIconHover('address', true)"
                                                        @mouseleave="setCopyIconHover('address', false)"
                                                    >
                                                        <v-icon small>
                                                            {{ copySuccessState.address ? 'mdi-check' : 'mdi-content-copy' }}
                                                        </v-icon>
                                                    </v-btn>
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-icon class="mr-3">
                                                <v-icon>mdi-email</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content
                                                @mouseenter="setCopyIconHover('email', true)"
                                                @mouseleave="setCopyIconHover('email', false)"
                                            >
                                                <v-list-item-title class="d-flex align-center">
                                                    <span class="font-weight-bold mr-2">E-Mail:</span>
                                                    <span>{{ currentAngehoerigeData('email') }}</span>
                                                    
                                                    <!-- Copy icon for email -->
                                                    <v-btn
                                                        v-if="currentAngehoerigeData('email') && isClipboardSupported"
                                                        icon
                                                        small
                                                        class="ml-2"
                                                        :style="{ 
                                                            opacity: copyIconHover.email ? 1 : 0,
                                                            transition: 'opacity 0.2s ease'
                                                        }"
                                                        :color="copySuccessState.email ? 'green' : $store.state.theme.primary"
                                                        @click="copyToClipboard(currentAngehoerigeData('email'), 'email')"
                                                        @mouseenter="setCopyIconHover('email', true)"
                                                        @mouseleave="setCopyIconHover('email', false)"
                                                    >
                                                        <v-icon small>
                                                            {{ copySuccessState.email ? 'mdi-check' : 'mdi-content-copy' }}
                                                        </v-icon>
                                                    </v-btn>
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-icon class="mr-3">
                                                <v-icon>mdi-phone</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content
                                                @mouseenter="setCopyIconHover('phone', true)"
                                                @mouseleave="setCopyIconHover('phone', false)"
                                            >
                                                <v-list-item-title class="d-flex align-center">
                                                    <span class="font-weight-bold mr-2">Telefon:</span>
                                                    <span>{{ currentAngehoerigeData('telefon') }}</span>
                                                    
                                                    <!-- Copy icon for phone -->
                                                    <v-btn
                                                        v-if="currentAngehoerigeData('telefon') && isClipboardSupported"
                                                        icon
                                                        small
                                                        class="ml-2"
                                                        :style="{ 
                                                            opacity: copyIconHover.phone ? 1 : 0,
                                                            transition: 'opacity 0.2s ease'
                                                        }"
                                                        :color="copySuccessState.phone ? 'green' : $store.state.theme.primary"
                                                        @click="copyToClipboard(currentAngehoerigeData('telefon'), 'phone')"
                                                        @mouseenter="setCopyIconHover('phone', true)"
                                                        @mouseleave="setCopyIconHover('phone', false)"
                                                    >
                                                        <v-icon small>
                                                            {{ copySuccessState.phone ? 'mdi-check' : 'mdi-content-copy' }}
                                                        </v-icon>
                                                    </v-btn>
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-icon class="mr-3">
                                                <v-icon>mdi-cake-variant-outline</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content
                                                @mouseenter="setCopyIconHover('birthdate', true)"
                                                @mouseleave="setCopyIconHover('birthdate', false)"
                                            >
                                                <v-list-item-title class="d-flex align-center">
                                                    <span class="font-weight-bold mr-2">Geburtsdatum:</span>
                                                    <span>{{ currentAngehoerigeData('geburtsdatum') }}</span>
                                                    
                                                    <!-- Copy icon for birthdate -->
                                                    <v-btn
                                                        v-if="currentAngehoerigeData('geburtsdatum') && isClipboardSupported"
                                                        icon
                                                        small
                                                        class="ml-2"
                                                        :style="{ 
                                                            opacity: copyIconHover.birthdate ? 1 : 0,
                                                            transition: 'opacity 0.2s ease'
                                                        }"
                                                        :color="copySuccessState.birthdate ? 'green' : $store.state.theme.primary"
                                                        @click="copyToClipboard(currentAngehoerigeData('geburtsdatum'), 'birthdate')"
                                                        @mouseenter="setCopyIconHover('birthdate', true)"
                                                        @mouseleave="setCopyIconHover('birthdate', false)"
                                                    >
                                                        <v-icon small>
                                                            {{ copySuccessState.birthdate ? 'mdi-check' : 'mdi-content-copy' }}
                                                        </v-icon>
                                                    </v-btn>
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>

                                        <v-list-item>
                                            <v-list-item-icon class="mr-3">
                                                <v-icon>mdi-card-account-details</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content
                                                @mouseenter="setCopyIconHover('svnr', true)"
                                                @mouseleave="setCopyIconHover('svnr', false)"
                                            >
                                                <v-list-item-title class="d-flex align-center">
                                                    <span class="font-weight-bold mr-2">SV-Nr:</span>
                                                    <span>{{ currentAngehoerigeData('svnr') }}</span>
                                                    <span class="ml-2">({{
                                                        (insurer.find(i => i.id === currentAngehoerigeData('versicherungsträger')) || {}).name || ''
                                                    }})</span>
                                                    
                                                    <!-- Copy icon for svnr -->
                                                    <v-btn
                                                        v-if="currentAngehoerigeData('svnr') && isClipboardSupported"
                                                        icon
                                                        small
                                                        class="ml-2"
                                                        :style="{ 
                                                            opacity: copyIconHover.svnr ? 1 : 0,
                                                            transition: 'opacity 0.2s ease'
                                                        }"
                                                        :color="copySuccessState.svnr ? 'green' : $store.state.theme.primary"
                                                        @click="copyToClipboard(currentAngehoerigeData('svnr'), 'svnr')"
                                                        @mouseenter="setCopyIconHover('svnr', true)"
                                                        @mouseleave="setCopyIconHover('svnr', false)"
                                                    >
                                                        <v-icon small>
                                                            {{ copySuccessState.svnr ? 'mdi-check' : 'mdi-content-copy' }}
                                                        </v-icon>
                                                    </v-btn>
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </template>

                                <!-- Manual Entry (if not linked) -->
                                <template v-else>
                                    <div class="d-flex align-center mb-2">
                                        <v-icon left color="primary">mdi-account-edit</v-icon>
                                        <span class="font-weight-medium">Manuelle Eingabe</span>
                                        <v-spacer></v-spacer>
                                        <v-btn
                                            v-if="canCreateFromFields(type)"
                                            small text color="success"
                                            @click="createAndLinkClient(type)"
                                        >
                                            <v-icon left>mdi-account-plus</v-icon>
                                            Neuen Klienten aus diesen Daten erstellen & verknüpfen
                                        </v-btn>
                                    </div>
                                    <div class="mb-2 text-caption">Trage die Daten des {{ title }} manuell ein, falls kein bestehender Klient verknüpft werden soll.</div>
                                    <!-- All manual entry fields as before -->
                                    <p class="d-flex align-center text-subtitle-1 font-weight-medium mb-5">
                                        Name
                                        <v-divider class="mx-2" />
                                        <v-btn small text color="primary" @click="copyClientLastname" class="caption">
                                            <v-icon small left>mdi-content-copy</v-icon>
                                            Nachname vom Klienten übernehmen
                                        </v-btn>
                                    </p>
                                    <v-row>
                                        <v-col class="my-0 py-0" cols="12" md="2" sm="3">
                                            <v-text-field :value="currentAngehoerigeData('titel_vorgestellt')"
                                                @input="newValue => handleManualInput('titel_vorgestellt', newValue)"
                                                label="Titel (vorgestellt)"
                                                dense outlined :disabled="!!linkedClient[type]" />
                                        </v-col>
                                        <v-col class="my-0 py-0" cols="12" md="4" sm="3">
                                            <v-text-field :value="currentAngehoerigeData('vorname')"
                                                @input="newValue => handleManualInput('vorname', newValue)"
                                                label="Vorname" dense outlined
                                                :rules="nameRules" :disabled="!!linkedClient[type]" />
                                        </v-col>
                                        <v-col class="my-0 py-0" cols="12" md="4" sm="3">
                                            <v-text-field :value="currentAngehoerigeData('nachname')"
                                                @input="newValue => handleManualInput('nachname', newValue)"
                                                label="Nachname" dense outlined
                                                :rules="nameRules" :disabled="!!linkedClient[type]" />
                                        </v-col>
                                        <v-col class="my-0 py-0" cols="12" md="2" sm="3">
                                            <v-text-field :value="currentAngehoerigeData('titel_nachgestellt')"
                                                @input="newValue => handleManualInput('titel_nachgestellt', newValue)"
                                                label="Titel (nachgestellt)" dense outlined :disabled="!!linkedClient[type]" />
                                        </v-col>
                                    </v-row>
                                    <p class="d-flex align-center text-subtitle-1 font-weight-medium mt-4 mb-5">
                                        Adresse
                                        <v-divider class="mx-2" />
                                        <v-btn small text color="primary" @click="copyClientAddress" class="caption">
                                            <v-icon small left>mdi-content-copy</v-icon>
                                            {{ $vuetify.breakpoint.xsOnly ? '' : 'Adresse' }} vom Klienten übernehmen
                                        </v-btn>
                                    </p>
                                    <v-row>
                                        <v-col class="my-0 py-0" cols="12" sm="5">
                                            <v-text-field :value="currentAngehoerigeData('adresse')"
                                                @input="newValue => handleManualInput('adresse', newValue)"
                                                label="Straße & Nr." dense outlined :rules="addressRules" :disabled="!!linkedClient[type]" />
                                        </v-col>
                                        <v-col class="my-0 py-0" cols="12" sm="2">
                                            <v-text-field :value="currentAngehoerigeData('plz')"
                                                @input="newValue => handleManualInput('plz', newValue)"
                                                label="PLZ" dense outlined :rules="addressRules" :disabled="!!linkedClient[type]" />
                                        </v-col>
                                        <v-col class="my-0 py-0" cols="12" sm="5">
                                            <v-text-field :value="currentAngehoerigeData('ort')"
                                                @input="newValue => handleManualInput('ort', newValue)"
                                                label="Ort" dense outlined :rules="addressRules" :disabled="!!linkedClient[type]" />
                                        </v-col>
                                    </v-row>
                                    <p class="d-flex align-center text-subtitle-1 font-weight-medium mt-4 mb-5">
                                        Kontaktdaten
                                        <v-divider class="mx-2" />
                                        <v-btn small text color="primary" @click="copyClientContactInfo" class="caption">
                                            <v-icon small left>mdi-content-copy</v-icon>
                                            {{ $vuetify.breakpoint.xsOnly ? '' : 'Kontaktdaten' }} vom Klienten übernehmen
                                        </v-btn>
                                    </p>
                                    <v-row>
                                        <v-col class="my-0 py-0" cols="12" sm="6">
                                            <v-text-field :value="currentAngehoerigeData('email')"
                                                @input="newValue => handleManualInput('email', newValue)"
                                                prepend-inner-icon="mdi-email-outline"
                                                label="E-Mail" dense outlined :disabled="!!linkedClient[type]" />
                                        </v-col>
                                        <v-col class="my-0 py-0" cols="12" sm="6">
                                            <v-text-field :value="currentAngehoerigeData('telefon')"
                                                @input="newValue => handleManualInput('telefon', newValue)"
                                                prepend-inner-icon="mdi-phone-in-talk"
                                                label="Telefon" dense outlined :disabled="!!linkedClient[type]" />
                                        </v-col>
                                    </v-row>
                                    <p class="d-flex align-center text-subtitle-1 font-weight-medium mt-4 mb-5">
                                        Versicherungsinformationen
                                        <v-divider class="ml-2" />
                                    </p>
                                    <v-row>
                                        <v-col class="my-0 py-0" cols="12" sm="4">
                                            <v-text-field :value="currentAngehoerigeData('geburtsdatum')"
                                                @input="newValue => handleBirthDateManualInput(newValue)" 
                                                dense outlined
                                                label="Geburtsdatum" hint="Format: TT.MM.JJJJ" prepend-inner-icon="mdi-calendar"
                                                :rules="[rules_birthday.datum]" maxlength="10" :disabled="!!linkedClient[type]" />
                                        </v-col>
                                        <v-col class="my-0 py-0" cols="12" sm="4">
                                            <v-text-field :value="currentAngehoerigeData('svnr')"
                                                @input="newValue => handleManualInput('svnr', newValue)"
                                                label="Sozialversicherungs-Nr." dense
                                                outlined hint="Format: NNNN TTMMJJ" :rules="svnrRules" :disabled="!!linkedClient[type]" />
                                        </v-col>
                                        <v-col class="my-0 py-0" cols="12" sm="4">
                                            <v-autocomplete :value="currentAngehoerigeData('versicherungsträger')"
                                                @change="newValue => handleManualInput('versicherungsträger', newValue)"
                                                :items="insurer"
                                                item-text="displayName" item-value="id" label="Versicherungsträger" dense outlined :disabled="!!linkedClient[type]" />
                                        </v-col>
                                    </v-row>
                                </template>
                            </v-card>            
                        </v-col>
                    </v-row>
                    
                </v-form>
            </v-card-text>
            <v-card-actions class="px-6 pb-5">
                <v-spacer></v-spacer>
                <v-btn :color="$store.state.theme.primary" text @click="close" :disabled="saving">Abbrechen</v-btn>
                <v-btn :color="$store.state.theme.green" outlined :disabled="!valid" @click="save" :loading="saving">
                    Speichern
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import cipher from '../helpers/cipher.js';
import connector from '../helpers/supabase-connector.js';
import FamilyRelationshipValidator, { RELATIONSHIP_TYPES } from '../helpers/FamilyRelationshipValidator.js';
import ClipboardMixin from '../mixins/ClipboardMixin.js';

export default {
    name: 'PersonEditDialog',
    mixins: [ClipboardMixin],
    props: {
        session: {
            type: Object,
            required: true
        },
        value: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: null,
            validator: value => value === null || ['vater', 'mutter', 'partner'].includes(value)
        },
        editedItem: {
            type: Object,
            required: true
        },
        insurer: {
            type: Array,
            default: () => []
        },
        rules_birthday: {
            type: Object,
            required: true
        },
        saving_persons: {
            type: Boolean,
            default: false
        },
        clientOptions: {
         type: Array,
         default: () => []
       },
    },

    data() {
        return {
            valid: true,
            requiredRule: [v => !!v || 'Dieses Feld ist erforderlich'],
            relationshipTypes: ['vater', 'mutter', 'partner'],
            relationshipTypeMap: {
                'vater': RELATIONSHIP_TYPES.FATHER,
                'mutter': RELATIONSHIP_TYPES.MOTHER,
                'partner': RELATIONSHIP_TYPES.PARTNER
            },
            linkedClient: { vater: null, mutter: null, partner: null },
            searchLinkedClient: { vater: '', mutter: '', partner: '' },
            // Local copy of editedItem - all changes are made here until save
            // This ensures cancel truly discards changes (no parent state modification)
            localEditedItem: null,
            validator: null,
            relationshipConflicts: [],
        }
    },

    computed: {
        show: {
            get() {
                return this.value;
            },
            set(value) {
                // Only emit the input event - let watchers handle side effects
                this.$emit('input', value);
            }
        },
        title() {
            const titles = {
                vater: 'Vater',
                mutter: 'Mutter',
                partner: 'Partner'
            };
            return titles[this.type] || '';
        },
        saving() {
            return this.saving_persons;
        },

        nameRules() {
            // Required if either receiver or insured with is selected
            return (this.localEditedItem && (this.localEditedItem.rechnungs_empfänger === this.type || 
                   this.localEditedItem.mitversichert_bei === this.type))
                ? this.requiredRule
                : []
        },

        addressRules() {
            // Required only if receiver is selected
            return (this.localEditedItem && this.localEditedItem.rechnungs_empfänger === this.type)
                ? this.requiredRule
                : []
        },

        svnrRules() {
            // Required only if insured with is selected
            if (this.localEditedItem && this.localEditedItem.mitversichert_bei === this.type) {
                return this.requiredRule;
            }
            return [];
        },

        clientOptionsWithDisplayName() {
            // Ensure validator is initialized
            if (!this.validator && this.clientOptions?.length > 0) {
                this.initializeValidator();
            }
            
            // Base clients (exclude self)
            const baseClients = this.clientOptions.filter(c => c.id !== this.editedItem.id);
            
            // Add display names, conflict info, and status chips
            return baseClients.map(c => {
                const conflicts = this.getClientConflicts(c.id);
                return {
                    ...c,
                    displayName: `${c.nachname || ''} ${c.vorname || ''}`.trim(),
                    disabled: conflicts.length > 0,
                    conflicts: conflicts,
                    statusChips: this.getStatusChips(c.id)
                };
            }).sort((a, b) => {
                // Sort: available first, then disabled, then alphabetically
                if (a.disabled !== b.disabled) {
                    return a.disabled ? 1 : -1;
                }
                const nameA = (a.displayName || '').toLowerCase();
                const nameB = (b.displayName || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        },

        currentAngehoerigeData() {
            return (fieldSuffix) => {
                if (!this.type) {
                    return '';
                }

                const linkedId = this.linkedClient[this.type];
                if (linkedId) {
                    const client = this.clientOptionsWithDisplayName.find(c => c.id === linkedId);
                    if (client) {
                        const value = client[fieldSuffix];
                        return value === undefined || value === null ? '' : value;
                    }
                }
                
                const fieldKeyForLocalItem = `${this.type}_${fieldSuffix}`;
                return this.localEditedItem && (this.localEditedItem[fieldKeyForLocalItem] !== undefined && this.localEditedItem[fieldKeyForLocalItem] !== null) 
                       ? this.localEditedItem[fieldKeyForLocalItem] 
                       : '';
            };
        },
        showPartnerWarning() {
            if (this.type !== 'partner') return false;
            const currentLinked = this.editedItem.fk_partner_id;
            const selectedLinked = this.linkedClient.partner;
            // Fall 1: Der aktuelle Klient hat einen Partner, und es wird ein anderer ausgewählt
            if (
                currentLinked &&
                selectedLinked &&
                currentLinked !== selectedLinked
            ) {
                return true;
            }
            // Fall 2: Der aktuelle Klient hat KEINEN Partner, aber der ausgewählte Partner ist schon mit jemand anderem verknüpft
            if (
                !currentLinked &&
                selectedLinked &&
                this.selectedPartnerCurrentPartnerId &&
                this.selectedPartnerCurrentPartnerId !== this.editedItem.id
            ) {
                return true;
            }
            return false;
        },
        currentClientName() {
            // Name des aktuellen Klienten (editedItem)
            return this.editedItem.vorname + ' ' + this.editedItem.nachname;
        },
        previousPartnerName() {
            // Name des bisherigen Partners (über fk_partner_id)
            if (!this.editedItem.fk_partner_id) return '';
            const partner = this.clientOptionsWithDisplayName.find(
                c => c.id === this.editedItem.fk_partner_id
            );
            if (!partner) return '';
            return partner.vorname + ' ' + partner.nachname;
        },
        // ID des aktuellen Partners des ausgewählten Partners
        selectedPartnerCurrentPartnerId() {
            if (!this.linkedClient.partner) return null;
            const selectedPartner = this.clientOptionsWithDisplayName.find(
                c => c.id === this.linkedClient.partner
            );
            return selectedPartner ? selectedPartner.fk_partner_id : null;
        },
        // Name des bisherigen Partners des ausgewählten Partners
        selectedPartnerCurrentPartnerName() {
            const partnerId = this.selectedPartnerCurrentPartnerId;
            if (!partnerId || partnerId === this.editedItem.id) return '';
            const partner = this.clientOptionsWithDisplayName.find(
                c => c.id === partnerId
            );
            if (!partner) return '';
            return [partner.titel_vorgestellt, partner.vorname, partner.nachname, partner.titel_nachgestellt].filter(Boolean).join(' ');
        },


    },

    created() {
        this.initializeValidator();
    },

    watch: {
        editedItem: {
            handler(newVal) {
                // Initialize localEditedItem when it doesn't exist (component mount or dialog fresh open)
                if (!this.localEditedItem) {
                    this.localEditedItem = newVal ? JSON.parse(JSON.stringify(newVal)) : {};
                    this.initializeLinkedClient();
                }
                // When localEditedItem exists and dialog is open, only sync parent-controlled fields
                else if (newVal && this.show) {
                    // Only update fields that parent explicitly manages via checkboxes
                    // This preserves all manual input data in the form fields
                    this.localEditedItem.rechnungs_empfänger = newVal.rechnungs_empfänger;
                    this.localEditedItem.mitversichert_bei = newVal.mitversichert_bei;
                }
            },
            deep: true,
            immediate: true
        },
        type() {
            this.initializeLinkedClient();
        },
        
        clientOptions: {
            handler() {
                this.initializeValidator();
            }
        },
        
        // Watch for dialog state changes to manage local state lifecycle
        value(newVal, oldVal) {
            // Dialog is closing - clean up local state
            if (oldVal && !newVal) {
                this.localEditedItem = null;
                this.$emit('update:type', null);
            }
            // Dialog is opening - initialize local copy from parent's editedItem
            else if (!oldVal && newVal && this.editedItem) {
                this.localEditedItem = JSON.parse(JSON.stringify(this.editedItem));
                this.initializeLinkedClient();
            }
        }
    },

    methods: {
        copyClientLastname() {
            const personType = this.type;
            if (this.localEditedItem) {
                this.$set(this.localEditedItem, `${personType}_nachname`, this.localEditedItem.nachname);
            }
        },

        copyClientAddress() {
            const personType = this.type;
            if (this.localEditedItem) {
                this.$set(this.localEditedItem, `${personType}_adresse`, this.localEditedItem.adresse);
                this.$set(this.localEditedItem, `${personType}_plz`, this.localEditedItem.plz);
                this.$set(this.localEditedItem, `${personType}_ort`, this.localEditedItem.ort);
            }
        },

        copyClientContactInfo() {
            const personType = this.type;
            if (this.localEditedItem) {
                this.$set(this.localEditedItem, `${personType}_email`, this.localEditedItem.email);
                this.$set(this.localEditedItem, `${personType}_telefon`, this.localEditedItem.telefon);
            }
        },

        // Validator helper methods
        getCurrentClientData() {
            return {
                fk_vater_id: this.linkedClient.vater,
                fk_mutter_id: this.linkedClient.mutter,
                fk_partner_id: this.linkedClient.partner
            };
        },


        getClientConflicts(clientId) {
            if (!this.validator || !this.type) return [];
            
            const relationshipType = this.relationshipTypeMap[this.type];
            if (!relationshipType) return [];

            const currentClientData = this.getCurrentClientData();
            const validation = this.validator.validateRelationship(
                this.editedItem.id,
                relationshipType,
                clientId,
                currentClientData
            );
            
            return validation.valid ? [] : validation.conflicts.map(c => c.message);
        },

        getStatusChips(clientId) {
            const currentData = this.getCurrentClientData();
            const chips = [];
            
            // Check if this client is already linked to current client
            const client = this.clientOptions.find(c => c.id === clientId);
            if (!client) return chips;

            // Partner relationship (bidirectional - only show once)
            if (currentData.fk_partner_id === clientId || client.fk_partner_id === this.editedItem.id) {
                chips.push({ 
                    type: 'partner', 
                    label: 'Partner', 
                    icon: 'mdi-human-male-female'
                });
            }
            
            // Parent relationships from current client perspective
            if (currentData.fk_vater_id === clientId) {
                chips.push({ 
                    type: 'father', 
                    label: 'Vater', 
                    icon: 'mdi-human-male-girl'
                });
            }
            if (currentData.fk_mutter_id === clientId) {
                chips.push({ 
                    type: 'mother', 
                    label: 'Mutter', 
                    icon: 'mdi-human-female-girl'
                });
            }

            // Child relationship (if this client has current client as parent)
            if (client.fk_vater_id === this.editedItem.id || client.fk_mutter_id === this.editedItem.id) {
                chips.push({ 
                    type: 'child', 
                    label: 'Kind', 
                    icon: 'mdi-account-child-outline'
                });
            }
            
            return chips;
        },

        initializeValidator() {
            if (this.clientOptions && this.clientOptions.length > 0) {
                this.validator = new FamilyRelationshipValidator(this.clientOptions);
            }
        },

        validateRelationshipChange(relationshipType, selectedClientId) {
            if (!this.validator || !selectedClientId) {
                this.relationshipConflicts = [];
                return;
            }

            const mappedType = this.relationshipTypeMap[relationshipType];
            if (!mappedType) return;

            const currentClientData = this.getCurrentClientData();
            const validation = this.validator.validateRelationship(
                this.editedItem.id,
                mappedType,
                selectedClientId,
                currentClientData
            );

            this.relationshipConflicts = validation.conflicts || [];

            if (!validation.valid) {
                const errorMessages = validation.conflicts.map(c => c.message).join(' ');
                this.$emit('showError', { message: errorMessages });
            }
        },

        validateRelationships() {
            if (!this.validator) return true; // Fallback

            const currentClientData = this.getCurrentClientData();
            const allErrors = [];

            // Check each relationship for conflicts
            for (const [type, clientId] of Object.entries(this.linkedClient)) {
                const mappedType = this.relationshipTypeMap[type];
                if (clientId && mappedType) {
                    const validation = this.validator.validateRelationship(
                        this.editedItem.id,
                        mappedType,
                        clientId,
                        currentClientData
                    );

                    if (!validation.valid) {
                        allErrors.push(...validation.conflicts.map(c => c.message));
                    }
                }
            }

            if (allErrors.length > 0) {
                this.$emit('showError', { 
                    message: `Beziehungskonflikte gefunden: ${allErrors.join(', ')}` 
                });
                return false;
            }

            return true;
        },

        handleBirthDateManualInput(value) {
            const personType = this.type;
            if (!this.localEditedItem) return;

            if (!value) {
                this.$set(this.localEditedItem, `${personType}_geburtsdatum`, '');
                return;
            }

            // Remove all non-digits
            let cleaned = value.toString().replace(/\D/g, '');

            // Limit to 8 digits
            cleaned = cleaned.slice(0, 8);

            // Format with dots
            let formatted = cleaned;
            if (cleaned.length > 0) {
                formatted = cleaned.replace(/^(\d{0,2})(\d{0,2})(\d{0,4}).*/, (match, d, m, y) => {
                    let result = '';
                    if (d) result += d;
                    if (m) result += '.' + m;
                    if (y) result += '.' + y;
                    return result;
                });
            }
            this.$set(this.localEditedItem, `${personType}_geburtsdatum`, formatted);
        },

        close() {
            // First update the v-model to trigger the show setter
            this.show = false;
            // Then emit the close event for parent to handle any additional logic
            this.$emit('close', true);
        },

        save() {
            if (!this.localEditedItem) {
                 return;
            }
            if (this.$refs.form.validate() && this.validateRequiredFields(this.localEditedItem) && this.validateRelationships()) {

                // Build update object with only the fields this dialog is responsible for
                const finalItemToEmit = {};
                
                // Only include checkbox fields if they've changed or if this person is involved
                // This prevents overwriting other persons' selections with unchanged values
                if (this.localEditedItem.rechnungs_empfänger === this.type || 
                    this.editedItem.rechnungs_empfänger === this.type) {
                    finalItemToEmit.rechnungs_empfänger = this.localEditedItem.rechnungs_empfänger;
                }
                
                if (this.localEditedItem.mitversichert_bei === this.type || 
                    this.editedItem.mitversichert_bei === this.type) {
                    finalItemToEmit.mitversichert_bei = this.localEditedItem.mitversichert_bei;
                }

                // Add foreign key for the current person type
                finalItemToEmit[`fk_${this.type}_id`] = this.linkedClient[this.type] || null;

                const fieldsToProcessForRelative = [
                    'titel_vorgestellt', 'vorname', 'nachname', 'titel_nachgestellt',
                    'email', 'telefon', 'adresse', 'plz', 'ort',
                    'geburtsdatum', 'svnr', 'versicherungsträger'
                ];

                if (this.linkedClient[this.type]) {
                    // Data from linked client
                    // we copy it over to the fields of the current person type, so that when it is unliked again, the data is still there
                    const linkedClientData = this.clientOptionsWithDisplayName.find(c => c.id === this.linkedClient[this.type]);
                    if (linkedClientData) {
                        fieldsToProcessForRelative.forEach(fieldKey => {
                            const valueToCopy = linkedClientData[fieldKey];
                            finalItemToEmit[`${this.type}_${fieldKey}`] = valueToCopy === undefined ? null : valueToCopy;
                        });
                    }
                } else {
                    // Data from manual input (localEditedItem)
                    fieldsToProcessForRelative.forEach(fieldKey => {
                        const valueFromLocal = this.localEditedItem[`${this.type}_${fieldKey}`];
                        finalItemToEmit[`${this.type}_${fieldKey}`] = valueFromLocal === undefined ? null : valueFromLocal;
                    });
                }
                
                this.$emit('save', finalItemToEmit);
            }
        },

        validateRequiredFields(itemToValidate) {
            const personType = this.type;
            if (!itemToValidate) return false;

            // Hilfsfunktion: Holt Wert aus verlinktem Klienten oder aus itemToValidate
            const getField = (field) => {
                // Wenn verlinkt, hole Wert aus verlinktem Klienten
                if (this.linkedClient[personType]) {
                    const linked = this.clientOptionsWithDisplayName.find(
                        c => c.id === this.linkedClient[personType]
                    );
                    return linked ? linked[field] : '';
                }
                // Sonst aus dem lokalen Objekt
                return itemToValidate[`${personType}_${field}`];
            };

            if (itemToValidate.rechnungs_empfänger === personType) {
                const requiredReceiverFields = [
                    `vorname`,
                    `nachname`,
                    `adresse`,
                    `plz`,
                    `ort`
                ];
                const missingReceiverFields = requiredReceiverFields.filter(field =>
                    !getField(field)
                );
                if (missingReceiverFields.length > 0) {
                    this.$emit('showError', {
                        message: `Bitte füllen Sie alle notwendigen Felder (Vorname, Nachname, Adresse, PLZ und Ort) für den Rechnungsempfänger "${this.title}" aus.`
                    });
                    return false;
                }
            }

            // Check insured with validation  
            if (itemToValidate.mitversichert_bei === personType) {
                const requiredInsuredFields = [
                    `vorname`,
                    `nachname`,
                    `svnr`
                ];
                const missingInsuredFields = requiredInsuredFields.filter(field =>
                    !getField(field)
                );
                if (missingInsuredFields.length > 0) {
                    this.$emit('showError', {
                        message: `Bitte füllen Sie alle notwendigen Felder (Vorname, Nachname, und Sozialversicherungsnummer) für den Versicherten "${this.title}" aus.`
                    });
                    return false;
                }
            }
            return true;
        },

        // Handle checkbox changes locally - only one recipient can be selected across all dialogs
        updateLocalRecipient(isChecked) {
            if (!this.localEditedItem) return;
            
            if (isChecked) {
                this.localEditedItem.rechnungs_empfänger = this.type;
            } else {
                // Only set to null if this person was actually the recipient
                // Otherwise keep the existing value (could be another person or institution)
                if (this.localEditedItem.rechnungs_empfänger === this.type) {
                    this.localEditedItem.rechnungs_empfänger = null;
                }
            }
            
            // Trigger form validation to show/hide required field errors
            this.$nextTick(() => {
                this.$refs.form?.validate();
            });
        },

        // Handle checkbox changes locally - only one person can be insured with
        updateLocalInsuredWith(isChecked) {
            if (!this.localEditedItem) return;
            
            if (isChecked) {
                this.localEditedItem.mitversichert_bei = this.type;
            } else {
                // Only set to null if this person was actually insured with
                // Otherwise keep the existing value (could be another person)
                if (this.localEditedItem.mitversichert_bei === this.type) {
                    this.localEditedItem.mitversichert_bei = null;
                }
            }
            
            // Trigger form validation to show/hide required field errors
            this.$nextTick(() => {
                this.$refs.form?.validate();
            });
        },

        onLinkedClientChange(type) {
            const currentType = type || this.type;
            const fkField = `fk_${currentType}_id`;
            
            // Validate the relationship change
            if (this.linkedClient[currentType]) {
                this.validateRelationshipChange(currentType, this.linkedClient[currentType]);
            } else {
                this.relationshipConflicts = [];
            }
            
            if (this.localEditedItem) {
                 this.$set(this.localEditedItem, fkField, this.linkedClient[currentType] || null);
            }
            this.$nextTick(() => {
                this.$refs.form?.validate();
            });
        },
        
        unlinkClient(type) {
            const currentType = type || this.type;
            this.linkedClient[currentType] = null;
            const fkField = `fk_${currentType}_id`;
            if (this.localEditedItem) {
                this.$set(this.localEditedItem, fkField, null);
            }
            this.$nextTick(() => {
                this.$refs.form?.validate();
            });
        },

        goToLinkedClient(clientId) {
            this.$router.push({ path: `/klienten/${clientId}` });
            this.close();
        },
        
        canCreateFromFields(type) {
            const currentType = type || this.type;
            return this.localEditedItem && 
                   this.localEditedItem[`${currentType}_vorname`] && 
                   this.localEditedItem[`${currentType}_nachname`];
        },
        async createAndLinkClient() {

            const newClientData = {
                uid: this.session.user.id,
            };
            const fieldsToCopy = [
                'titel_vorgestellt', 'vorname', 'nachname', 'titel_nachgestellt',
                'email', 'telefon', 'adresse', 'plz', 'ort',
                'geburtsdatum', 'svnr', 'versicherungsträger'
            ];

            fieldsToCopy.forEach(fieldSuffix => {
                const fieldKey = `${this.type}_${fieldSuffix}`;
                if (this.localEditedItem[fieldKey] !== undefined && this.localEditedItem[fieldKey] !== null) {
                    newClientData[fieldSuffix] = this.localEditedItem[fieldKey];
                } else {
                    newClientData[fieldSuffix] = null; // Ensure all fields exist for encryption
                }
            });

            // Validate required fields for a new client (vorname, nachname)
            if (!newClientData.vorname || !newClientData.nachname) {
                this.$emit('showError', { 
                    message: "Vorname und Nachname sind für den neuen Klienten erforderlich.",
                    timeout: 8000
                });
                return;
            }

            try {
                const encryptedClientData = await cipher.encryptObject(this.$store.state.aes_key, newClientData);
                const inserted_id = await connector.insert(this, 'klienten', encryptedClientData);

                if (!inserted_id) {
                    this.$emit('showError', { 
                        message: "Fehler beim Erstellen des neuen Klienten. Bitte versuche es erneut.",
                        timeout: 8000
                    });
                    return;
                }

                // New client created successfully
                this.$set(this.linkedClient, this.type, inserted_id);
                const fkField = `fk_${this.type}_id`;
                this.$set(this.localEditedItem, fkField, inserted_id);

                this.$emit('showInfo', { 
                    message: `${newClientData.nachname} ${newClientData.vorname} wurde erstellt und zur Verknüpfung ausgewählt.`,
                    timeout: 8000
                });
                this.$emit('client-created', { ...newClientData, id: inserted_id }); // Emit event with new client data

                this.$nextTick(() => {
                    this.$refs.form?.validate();
                });

            } catch (error) {
                this.$emit('showError', { 
                    message: `Ein Fehler ist aufgetreten. Bitte versuche es erneut.`,
                    timeout: 8000,
                    error: error
                });
            } 
        },
        getLinkedClientName() {
            const client = this.clientOptionsWithDisplayName.find(c => c.id === this.linkedClient[this.type]);
            return client ? (client.titel_vorgestellt ?? '') + (client.displayName ?? '') + (client.titel_nachgestellt ?? '') : '';
        },
        handleManualInput(fieldSuffix, value) {
            const fieldKey = `${this.type}_${fieldSuffix}`;
            if (this.localEditedItem) {
                this.$set(this.localEditedItem, fieldKey, value);
            }
        },
        initializeLinkedClient() {
            if (!this.localEditedItem || !this.relationshipTypes) return;
            this.relationshipTypes.forEach(type => {
                const fkField = `fk_${type}_id`;
                this.$set(this.linkedClient, type, this.localEditedItem[fkField] || null);
            });
        },

    },
}
</script>